import { CrateStateEnum } from 'src/models/Crate';
import { V3ProductsDataSourceService } from 'src/app/core/data-sources/v3/products/v3-products-data-source.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription, of, from } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { StockDataSourceService } from 'src/app/core/data-sources/stock-data-source.service';
import { ActionService } from 'src/app/core/services/action.service';
import { ErrorService, ErrorsEnum } from 'src/app/core/services/error.service';
import { ProductsInterface } from 'src/app/pages/stock/stock-inventory-sidenav/stock-inventory-sidenav.component';
import { ActionsEnum } from 'src/app/core/data-sources/action-data-source.service';
import * as XLSX from 'xlsx';

export interface ManualPickingItem {
  sku: string;
  description?: string;
  quantity: number;
  availability?: boolean;
  availableQty?: number;
  checkingAvailability?: boolean;
}

@Component({
  selector: 'app-manual-picking-modal-v2',
  templateUrl: './manual-picking-modal-v2.component.html',
  styleUrls: ['./manual-picking-modal-v2.component.scss']
})
export class ManualPickingModalV2Component implements OnInit, OnDestroy {
  title = "Cadastro de Ordens de Separação";
  private readonly orderNumberPattern = /^[\p{L}\p{N}_-]+$/u;
  displayedColumns = ['sku', 'description', 'quantity', 'availability', 'actions'];
  dataSource: MatTableDataSource<ManualPickingItem> = new MatTableDataSource<ManualPickingItem>([]);
  products: ProductsInterface[] = [];
  productInputSubscription$: Subscription;
  showTable = false;
  importedFileName: string | null = null;
  form: FormGroup = new FormGroup({
    sku: new FormControl(null, Validators.required),
    quantity: new FormControl(null, [Validators.required, Validators.min(1)]),
    description: new FormControl(null),
    orderNumber: new FormControl(null, [Validators.required, Validators.pattern(this.orderNumberPattern)])
  });

  constructor(
    private stockService: StockDataSourceService,
    public dialogRef: MatDialogRef<ManualPickingModalV2Component>,
    private actionService: ActionService,
    private v3productsService: V3ProductsDataSourceService,
    private errorService: ErrorService
  ) { }

  ngOnInit(): void {
    this.productInputSubscription$ = this.form.controls.sku.valueChanges.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((search) => {
        if (typeof search !== 'string' || !search || search.length < 3) {
          this.products = [];
          return of([]);
        }
        return from(this.stockService.getSearchedProducts(search));
      })
    ).subscribe((res: ProductsInterface[]) => {
      this.products = res || [];
    });
  }

  ngOnDestroy(): void {
    this.productInputSubscription$.unsubscribe();
  }

  async checkAvailability(skus: string): Promise<number> {
    if (!skus) return 0;
    try {
      const groundPositionType = ['STOCK', 'PICKING', 'PICKING_MISCELLANEOUS'];
      const palletStates = [CrateStateEnum.STORED];
      const params = { skus: [skus], palletStates };
      const res = await this.v3productsService.getV3ProductsCount(groundPositionType, params);
      const products = res || [];
      const product = products.find(p => p.sku === skus || (p.sku && p.sku.toString() === skus.toString()));
      const availableQty = product ? (product.quantity || 0) : 0;
      return availableQty;
    } catch (e) {
      return 0;
    }
  }

  addManuallyToTable(product) {
    const item = {
      sku: product.sku.sku ?? product.sku,
      description: product.sku.name || '---',
      quantity: product.quantity || 1,
      availability: product.availability !== undefined ? product.availability : undefined,
      checkingAvailability: true
    };

    const current = this.dataSource.data || [];
    this.dataSource.data = [...current, item];
    this.showTable = true;
    this.form.controls.sku.reset();
    this.form.controls.quantity.reset();
    this.form.controls.sku.markAsUntouched();
    this.form.controls.quantity.markAsUntouched();

    const index = this.dataSource.data.length - 1;
    const qty = item.quantity || 1;
    this.checkAvailability(item.sku).then((availableQty) => {
      const data = this.dataSource.data || [];
      if (data[index]) {
        data[index].availableQty = availableQty;
        data[index].availability = availableQty >= qty;
        data[index].checkingAvailability = false;
        this.dataSource = new MatTableDataSource(data);
      }
    }).catch(() => {
      const data = this.dataSource.data || [];
      if (data[index]) {
        data[index].availableQty = 0;
        data[index].availability = false;
        data[index].checkingAvailability = false;
        this.dataSource = new MatTableDataSource(data);
      }
    });
  }

  async importToTable(products): Promise<void> {
    if (!products || !products.length) return;

    const items: ManualPickingItem[] = products.map(product => {
      return {
        sku: product.sku,
        description: product.description,
        quantity: product.quantity,
        availability: undefined,
        checkingAvailability: true
      };
    });

    this.dataSource.data = [...this.dataSource.data, ...items];
    this.showTable = true;

    const availabilityPromises = items.map((item, idx) => 
      this.checkAvailability(item.sku).then(availableQty => ({ idx, availableQty, success: true }))
        .catch(() => ({ idx, availableQty: 0, success: false }))
    );

    const results = await Promise.all(availabilityPromises);
    const data = this.dataSource.data || [];
    const baseIndex = data.length - items.length;
    
    results.forEach(result => {
      const dataIndex = baseIndex + result.idx;
      if (data[dataIndex]) {
        data[dataIndex].availableQty = result.availableQty;
        data[dataIndex].availability = result.availableQty >= (data[dataIndex].quantity || 1);
        data[dataIndex].checkingAvailability = false;
      }
    });

    this.dataSource = new MatTableDataSource(data);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input || !input.files || !input.files.length) return;
    const file = input.files[0];
    this.importedFileName = file.name;

    this.form.reset();
    this.dataSource.data = [];
    this.showTable = false;

    this.parseXlsx(file).then((rows) => {
      if (!rows || !rows.length) {
        this.errorService.openErrorSnackBarWithCustomMessage('Arquivo vazio ou formato inválido.');
        return;
      }

      const mapping = this.detectColumnMappings(rows);
      let orderNumber = '';
      const normalized = rows.map(row  => {
        const sku = mapping.skuKey ? row[mapping.skuKey].toString() : null;
        const description = mapping.descKey ? row[mapping.descKey].toString() : '---';
        const quantity = mapping.qtyKey ? Number(row[mapping.qtyKey]) || 0 : 0;
        orderNumber = mapping.orderKey ? row[mapping.orderKey]?.toString() : '';
        return { sku, description, quantity };
      });

      this.form.controls.orderNumber.setValue(orderNumber);
      this.validateImportedOrderNumber();

      this.importToTable(normalized).catch((err) => {
        console.error('Erro ao verificar disponibilidade:', err);
      });
    }).catch((err) => {
      this.errorService.openErrorSnackBarWithCustomMessage('Erro ao processar arquivo.');
    }).finally(() => {
      if (input) input.value = '';
    });
  }

  parseXlsx(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          const arr = Array.from(data).map((v) => String.fromCharCode(v)).join('');
          const workbook = XLSX.read(arr, { type: 'binary' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const json = XLSX.utils.sheet_to_json(worksheet, { defval: null });
          resolve(json);
        } catch (ex) {
          reject(ex);
        }
      };
      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    });
  }

  private detectColumnMappings(rows): { skuKey?: string; qtyKey?: string; descKey?: string; orderKey?: string } {
    const knownSku = ['códigodoitem', 'codigoitem', 'codigodoitem', 'código do item'];
    const knownQty = ['quantidade'];
    const knownDesc = ['descricao', 'descrição'];
    const knownOrder = ['numero ordem', 'número ordem', 'número da ordem'];

    if (!rows || !rows.length) return {};
    const first = rows[0];
    const keys = Object.keys(first || {});
    const mapping: any = {};

    keys.forEach((k) => {
      const nk = k.toLowerCase().trim();
      if (!mapping.skuKey && knownSku.some(s => nk.includes(s))) mapping.skuKey = k;
      if (!mapping.qtyKey && knownQty.some(s => nk.includes(s))) mapping.qtyKey = k;
      if (!mapping.descKey && knownDesc.some(s => nk.includes(s))) mapping.descKey = k;
      if (!mapping.orderKey && knownOrder.some(s => nk.includes(s))) mapping.orderKey = k;
    });

    return mapping;
  }

  deleteFromTable(index: number) {
    if (!this.dataSource || !this.dataSource.filteredData.length) return;
    this.dataSource.filteredData.splice(index, 1);
    this.dataSource = new MatTableDataSource(this.dataSource.filteredData);
  }

  buildProductsArray(): { sku: string | number, quantity: number }[] {
    if (!this.dataSource) return [];
    const rows = (this.dataSource.filteredData && this.dataSource.filteredData.length)
      ? this.dataSource.filteredData
      : (this.dataSource.data || []);
    return rows.map(r => ({ sku: r.sku, quantity: r.quantity || 0 }));
  }

  close() {
    this.form.reset();
    this.showTable = false;
    this.dialogRef.close();
  }

  async submit() {
    const orderNumberControl = this.form.controls.orderNumber;

    if (orderNumberControl.invalid) {
      orderNumberControl.markAsTouched();
      return;
    }

    const products = this.buildProductsArray();

    try {
      const description = this.form.value.description;

      const payload: any = {
        outOrderNumber: this.form.value.orderNumber.trim(),
        products
      };

      if (description?.length > 1) {
        payload.description = description.trim();
      }

      await this.actionService.createInternalOutOrder({
        type: ActionsEnum.CREATE_INTERNAL_OUT_ORDER,
        data: payload
      }, { disableAutoErrorHandler: true });

      this.showTable = false;
      this.dialogRef.close(true);
    } catch (e) {
      const type = e.error?.body?.type;
      const details = e.error?.body?.details;
      switch (type) {
        case ErrorsEnum.OUT_ORDER_ERROR_INVALID_PRODUCT:
          const sku = details && details.sku ? details.sku : null;
          const message = "Produtos duplicados não são permitidos.";
          this.errorService.openErrorSnackBarWithCustomMessage(`Produto inválido: ${sku || ''} - ${message}`);
          break;
        case ErrorsEnum.OUT_ORDER_ERROR_ALREADY_EXISTS:
          this.errorService.openErrorSnackBarWithCustomMessage('Número de ordem já utilizado.');
          if (this.form && this.form.controls && this.form.controls.orderNumber) {
            this.form.controls.orderNumber.setErrors({ alreadyExists: true });
          }
          break;
        default:
          this.errorService.openErrorSnackBar(e.error?.body);
      }
    }
  }

  displayedString(product) {
    return product ? (product.name + ' - ' + product.sku) : '';
  }

  private validateImportedOrderNumber(): void {
    const orderNumber = this.form.controls.orderNumber;

    if (orderNumber.invalid) {
      orderNumber.markAsTouched();
    }
  }

}
